package com.far.suller.sections

import androidx.compose.runtime.Composable
import com.far.suller.models.Experience
import com.far.suller.models.Section
import com.far.suller.models.Theme
import com.far.suller.util.Constants
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.icons.fa.FaBuilding
import com.varabyte.kobweb.silk.components.icons.fa.IconSize
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

private val AMBER_500 = Color.rgb(245, 158, 11)
private val DARK_CARD_BG = Color.rgb(22, 18, 15) // Darker brown/black for cards
private val CARD_BORDER = Color.rgba(245, 158, 11, 0.1f)

@Composable
fun experienceSectionV2() {
    val breakpoint = rememberBreakpoint()
    val isDesktop = breakpoint >= Breakpoint.MD

    Column(
        modifier = Modifier
            .id(Section.Experience.id)
            .fillMaxWidth()
            .padding(topBottom = if (isDesktop) 150.px else 80.px)
            .backgroundColor(Theme.Background.rgb)
            .styleModifier {
                // Subtle geometric pattern
                property(
                    "background-image",
                    "radial-gradient(circle at 2px 2px, rgba(255,255,255,0.01) 1px, transparent 0)",
                )
                property("background-size", "50px 50px")
            },
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        // Header
        Column(
            modifier = Modifier.margin(bottom = 100.px),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Span(
                attrs = Modifier
                    .fontFamily(Constants.FONT_FAMILY)
                    .fontSize(16.px)
                    .fontWeight(FontWeight.Bold)
                    .color(AMBER_500)
                    .margin(bottom = 12.px)
                    .styleModifier {
                        property("letter-spacing", "6px")
                        property("text-transform", "uppercase")
                    }
                    .toAttrs(),
            ) {
                Text("Experience")
            }

            P(
                attrs = Modifier
                    .fontFamily(Constants.FONT_FAMILY)
                    .fontSize(if (isDesktop) 54.px else 36.px)
                    .fontWeight(FontWeight.Black)
                    .color(Colors.White)
                    .margin(topBottom = 0.px)
                    .textAlign(TextAlign.Center)
                    .toAttrs(),
            ) {
                Text("Work History")
            }

            Box(
                modifier = Modifier
                    .margin(top = 16.px)
                    .width(80.px)
                    .height(3.px)
                    .backgroundColor(AMBER_500)
                    .borderRadius(2.px),
            )
        }

        // Timeline Container
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .maxWidth(Constants.SECTION_WIDTH.px)
                .padding(leftRight = if (isDesktop) 2.percent else 5.percent),
            contentAlignment = Alignment.TopCenter,
        ) {
            // Central Timeline Line
            if (isDesktop) {
                Box(
                    modifier = Modifier
                        .fillMaxHeight()
                        .width(2.px)
                        .backgroundColor(Color.rgba(245, 158, 11, 0.15f))
                        .margin(top = 20.px),
                )
            }

            Column(modifier = Modifier.fillMaxWidth()) {
                val experiences = Experience.entries.reversed()
                experiences.forEachIndexed { index, exp ->
                    ExperienceRowV2(index, exp, isDesktop)
                }
            }
        }
    }
}

@Composable
private fun ExperienceRowV2(index: Int, experience: Experience, isDesktop: Boolean) {
    val isEven = index % 2 == 0 // Alternating side logic

    if (isDesktop) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .margin(bottom = 60.px),
            verticalAlignment = Alignment.Top,
            horizontalArrangement = Arrangement.Center,
        ) {
            // Left Side
            Box(
                modifier = Modifier.weight(1f).fillMaxWidth(),
                contentAlignment = Alignment.TopEnd,
            ) {
                if (isEven) {
                    ExperienceCardV2(experience, isLeftSide = true)
                }
            }

            // Timeline Node (The Circle)
            Box(
                modifier = Modifier.width(120.px).margin(top = 40.px),
                contentAlignment = Alignment.Center,
            ) {
                TimelineNode(experience.number)
            }

            // Right Side
            Box(
                modifier = Modifier.weight(1f).fillMaxWidth(),
                contentAlignment = Alignment.TopStart,
            ) {
                if (!isEven) {
                    ExperienceCardV2(experience, isLeftSide = false)
                }
            }
        }
    } else {
        // Mobile Layout
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .margin(bottom = 60.px),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            TimelineNode(experience.number)
            Box(modifier = Modifier.height(24.px))
            ExperienceCardV2(experience, isLeftSide = false)
        }
    }
}

@Composable
private fun TimelineNode(number: String) {
    Box(
        modifier = Modifier
            .size(54.px)
            .backgroundColor(Theme.Background.rgb)
            .borderRadius(50.percent)
            .border(2.px, LineStyle.Solid, AMBER_500)
            .styleModifier {
                property("box-shadow", "0 0 20px rgba(245, 158, 11, 0.2)")
                property("z-index", "10")
            },
        contentAlignment = Alignment.Center,
    ) {
        Span(
            attrs = Modifier
                .fontFamily(Constants.FONT_FAMILY)
                .fontSize(16.px)
                .fontWeight(FontWeight.Bold)
                .color(Colors.White)
                .toAttrs(),
        ) {
            Text(number)
        }
    }
}

@Composable
private fun ExperienceCardV2(experience: Experience, isLeftSide: Boolean) {
    val isCurrent = experience.to == "Present"

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .maxWidth(520.px) // Limit width for better alignment
            .backgroundColor(DARK_CARD_BG)
            .borderRadius(20.px)
            .border(1.px, LineStyle.Solid, CARD_BORDER)
            .styleModifier {
                property("box-shadow", "0 10px 40px rgba(0,0,0,0.2)")
                property("position", "relative")
            },
    ) {
        // "Current Role" Badge
        if (isCurrent) {
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .top(0.px)
                    .right(0.px)
                    .backgroundColor(AMBER_500)
                    .padding(leftRight = 14.px, topBottom = 6.px)
                    .borderRadius(bottomLeft = 12.px, topRight = 20.px),
            ) {
                Span(
                    attrs = Modifier
                        .fontFamily(Constants.FONT_FAMILY)
                        .fontSize(11.px)
                        .fontWeight(FontWeight.Bold)
                        .color(Colors.White)
                        .styleModifier { property("text-transform", "uppercase") }
                        .toAttrs(),
                ) {
                    Text("Current")
                }
            }
        }

        Column(modifier = Modifier.padding(all = 32.px)) {
            // Date Range
            Span(
                attrs = Modifier
                    .fontFamily(Constants.FONT_FAMILY)
                    .fontSize(13.px)
                    .fontWeight(FontWeight.Bold)
                    .color(AMBER_500)
                    .margin(bottom = 12.px)
                    .toAttrs(),
            ) {
                Text("${experience.from} – ${experience.to}")
            }

            // Job Title
            P(
                attrs = Modifier
                    .fontFamily(Constants.FONT_FAMILY)
                    .fontSize(26.px)
                    .fontWeight(FontWeight.Black)
                    .color(Colors.White)
                    .margin(topBottom = 0.px)
                    .toAttrs(),
            ) {
                Text(experience.jobPosition)
            }

            // Company & Icon Row
            Row(
                modifier = Modifier
                    .margin(top = 16.px, bottom = 24.px)
                    .backgroundColor(Color.rgba(255, 255, 255, 0.03f))
                    .padding(leftRight = 14.px, topBottom = 8.px)
                    .borderRadius(8.px),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                FaBuilding(modifier = Modifier.margin(right = 10.px).color(AMBER_500), size = IconSize.SM)
                Span(
                    attrs = Modifier
                        .fontFamily(Constants.FONT_FAMILY)
                        .fontSize(15.px)
                        .fontWeight(FontWeight.Bold)
                        .color(Color.rgba(255, 255, 255, 0.7f))
                        .toAttrs(),
                ) {
                    Text(experience.company)
                }
            }

            // Bullet Points
            Column(modifier = Modifier.margin(bottom = 32.px)) {
                experience.description.filter { !it.contains("Role:") }.forEach { point ->
                    Row(modifier = Modifier.margin(bottom = 12.px)) {
                        Box(
                            modifier = Modifier
                                .size(6.px)
                                .backgroundColor(AMBER_500)
                                .margin(top = 8.px, right = 14.px)
                                .borderRadius(50.percent),
                        )
                        P(
                            attrs = Modifier
                                .margin(topBottom = 0.px)
                                .fontFamily(Constants.FONT_FAMILY)
                                .fontSize(14.px)
                                .color(Color.rgba(255, 255, 255, 0.5f))
                                .lineHeight(1.6)
                                .toAttrs(),
                        ) {
                            Text(point.replace("▪ ", "").trim())
                        }
                    }
                }
            }

            // Tech Stack Chips
            val techKeywords = listOf("Kotlin", "MVVM", "XML", "Azure DevOps", "Jetpack Compose", "Hilt", "Vue", "Svelte", "Android Studio", "Git", "Retrofit", "Firebase", "Unit Testing", "CI/CD")
            val tagsToShow = techKeywords.filter { keyword ->
                experience.description.any { it.contains(keyword, ignoreCase = true) }
            }

            if (tagsToShow.isNotEmpty()) {
                Row(
                    modifier = Modifier.fillMaxWidth().styleModifier { property("flex-wrap", "wrap") },
                    horizontalArrangement = Arrangement.Start,
                ) {
                    tagsToShow.forEach { tag ->
                        Box(
                            modifier = Modifier
                                .margin(right = 8.px, bottom = 8.px)
                                .backgroundColor(Color.rgba(245, 158, 11, 0.05f))
                                .padding(leftRight = 10.px, topBottom = 5.px)
                                .borderRadius(6.px)
                                .border(1.px, LineStyle.Solid, Color.rgba(245, 158, 11, 0.15f)),
                        ) {
                            Span(
                                attrs = Modifier
                                    .fontFamily(Constants.FONT_FAMILY)
                                    .fontSize(10.px)
                                    .fontWeight(FontWeight.Bold)
                                    .color(AMBER_500)
                                    .toAttrs(),
                            ) {
                                Text(tag)
                            }
                        }
                    }
                }
            }
        }
    }
}
